import { useState, useEffect } from "react";
import { Calendar, ChevronRight, Tag, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const categories = [
  { value: "all", label: "Tous", color: "bg-forest" },
  { value: "grossesse", label: "Grossesse", color: "bg-pink-500" },
  { value: "portee", label: "Portée", color: "bg-amber-500" },
  { value: "actualite", label: "Actualité", color: "bg-blue-500" },
  { value: "conseil", label: "Conseil", color: "bg-green-500" },
];

const getCategoryColor = (category) => {
  const cat = categories.find(c => c.value === category);
  return cat ? cat.color : "bg-forest";
};

const getCategoryLabel = (category) => {
  const cat = categories.find(c => c.value === category);
  return cat ? cat.label : category;
};

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

const BASE = process.env.REACT_APP_BACKEND_URL || '';
const resolveUrl = (url) => url?.startsWith('/api/uploads/') ? `${BASE}${url}` : url;

function formatDate(dateString) {
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
}

function BlogCard({ post, onClick }) {
  return (
    <article
      className="bg-white rounded-2xl overflow-hidden shadow-soft hover:-translate-y-1 transition-transform duration-300 cursor-pointer"
      onClick={onClick}
      data-testid={`blog-card-${post.id}`}
    >
      {/* Image */}
      {post.image_url ? (
        <div className="relative h-48 img-hover-zoom">
          <img
            src={resolveUrl(post.image_url)}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <Badge className={`absolute top-3 left-3 ${getCategoryColor(post.category)} text-white`}>
            {getCategoryLabel(post.category)}
          </Badge>
        </div>
      ) : (
        <div className="h-48 bg-sand/30 flex items-center justify-center">
          <ImageIcon className="w-12 h-12 text-sand" />
        </div>
      )}

      {/* Content */}
      <div className="p-5">
        <div className="flex items-center gap-2 text-forest/50 text-sm mb-2">
          <Calendar className="w-4 h-4" />
          <span className="font-body">{formatDate(post.published_at)}</span>
        </div>
        
        <h3 className="font-heading text-xl font-semibold text-forest mb-2 line-clamp-2">
          {post.title}
        </h3>
        
        <p className="font-body text-sm text-forest/70 line-clamp-3 mb-4">
          {post.content.substring(0, 150)}...
        </p>

        <span className="inline-flex items-center text-autumn font-body font-medium text-sm hover:underline">
          Lire la suite
          <ChevronRight className="w-4 h-4 ml-1" />
        </span>
      </div>
    </article>
  );
}

function BlogModal({ post, isOpen, onClose }) {
  if (!post) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-cream border-sand max-h-[90vh] overflow-y-auto">
        {post.image_url && (
          <img
            src={resolveUrl(post.image_url)}
            alt={post.title}
            className="w-full h-64 object-cover rounded-xl -mt-2"
          />
        )}
        
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <Badge className={`${getCategoryColor(post.category)} text-white`}>
              {getCategoryLabel(post.category)}
            </Badge>
            <span className="font-body text-sm text-forest/60">
              {formatDate(post.published_at)}
            </span>
          </div>
          <DialogTitle className="font-heading text-2xl sm:text-3xl font-bold text-forest text-left">
            {post.title}
          </DialogTitle>
        </DialogHeader>

        <div className="font-body text-forest/80 leading-relaxed whitespace-pre-line">
          {post.content}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function BlogSection() {
  const [posts, setPosts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedPost, setSelectedPost] = useState(null);
  const [visibleCount, setVisibleCount] = useState(4);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API}/blog`);
        if (response.ok) {
          const data = await response.json();
          setPosts(data);
        }
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      }
      setLoading(false);
    };
    fetchPosts();
  }, []);

  const filteredPosts = selectedCategory === "all"
    ? posts
    : posts.filter(post => post.category === selectedCategory);

  const displayedPosts = filteredPosts.slice(0, visibleCount);

  return (
    <section
      id="blog"
      data-testid="blog-section"
      className="relative py-24 lg:py-32 bg-cream-alt overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section header */}
        <div className="section-animate text-center mb-12">
          <span className="font-accent text-autumn text-xl">Notre journal</span>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-forest mt-2 mb-6">
            Actualités de l'Élevage
          </h2>
          <p className="font-body text-lg text-forest/70 max-w-2xl mx-auto">
            Suivez le quotidien de nos Sibériens : grossesses, naissances,
            évolution des chatons et conseils pratiques.
          </p>
        </div>

        {/* Category filters */}
        <div className="section-animate flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((cat) => (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? "default" : "outline"}
              className={`rounded-full font-body ${
                selectedCategory === cat.value
                  ? "bg-forest text-white hover:bg-forest-dark"
                  : "border-sand text-forest hover:bg-sand/30"
              }`}
              onClick={() => {
                setSelectedCategory(cat.value);
                setVisibleCount(4);
              }}
              data-testid={`blog-filter-${cat.value}`}
            >
              <Tag className="w-4 h-4 mr-1" />
              {cat.label}
            </Button>
          ))}
        </div>

        {/* Blog grid */}
        <div className="section-animate grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {displayedPosts.map((post) => (
            <BlogCard
              key={post.id}
              post={post}
              onClick={() => setSelectedPost(post)}
            />
          ))}
        </div>

        {/* Load more */}
        {visibleCount < filteredPosts.length && (
          <div className="section-animate text-center mt-10">
            <Button
              variant="outline"
              className="border-forest text-forest hover:bg-forest hover:text-white rounded-full px-8"
              onClick={() => setVisibleCount(prev => prev + 4)}
              data-testid="blog-load-more"
            >
              Voir plus d'articles
            </Button>
          </div>
        )}

        {/* Empty state */}
        {filteredPosts.length === 0 && (
          <div className="text-center py-12">
            <p className="font-body text-forest/60">
              Aucun article dans cette catégorie pour le moment.
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      <BlogModal
        post={selectedPost}
        isOpen={!!selectedPost}
        onClose={() => setSelectedPost(null)}
      />
    </section>
  );
}
